/**
 * AuthContext — token state and auth actions (login, signUp, logout).
 *
 * Responsibilities:
 * - Owns `token` React state and persistence to localStorage.
 * - Provides login/signUp/logout actions that mutate `token` and the `['currentUser']` query cache.
 * - Triggers the current-user fetch via useQuery (single request thanks to query-key dedup +
 *   staleTime: Infinity; one /user/me per session, refetched only after logout clears the cache).
 *
 * Decomposed concerns:
 * - JWT parsing → utils/auth/tokenUtils.ts (pure)
 * - Auto-logout timer → hooks/useAutoLogoutOnExpiry
 * - Locale + user persistence → hooks/useSyncSessionPreferences (subscribes to ['currentUser'] cache)
 *
 * Token expiration:
 * - Derived from the JWT's `exp` claim (client-side decode, not signature verification).
 * - Non-JWT or no-exp tokens degrade to non-expiring; server 401 remains the authority.
 * - isAuthenticated = has token AND not expired. Expired tokens trigger auto-logout via the hook.
 */
import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { User } from '../types';
import { apiService } from '../services';
// Adapts ServiceResult<T> to throw-on-failure so TanStack Query's queryFn can set error state.
import { unwrapServiceResultAsync } from '../services/utils/errorUtils';
import type { ProcessedError } from '../services/utils/errorHandler';
import { logger } from '../utils/logger';
import { TOKEN, LOCALE, USER } from '../constants';
import { getTokenExpiration, hasTokenExpired } from '../utils/auth/tokenUtils';
import { useAutoLogoutOnExpiry } from '../hooks/useAutoLogoutOnExpiry';
import { useSyncSessionPreferences } from '../hooks/useSyncSessionPreferences';

/** Query key for current user. Shared key = one request when multiple consumers mount (e.g. Strict Mode). */
const CURRENT_USER_QUERY_KEY = ['currentUser'] as const;

interface AuthContextType {
  user: User | null;
  token: string | null;
  /** JWT exp as ms timestamp, or null if not a JWT / no exp. Use for display or refresh logic. */
  tokenExpiresAt: number | null;
  login: (
    usernameOrEmail: string,
    password: string
  ) => Promise<{
    success: boolean;
    error?: string;
    processedError?: ProcessedError;
  }>;
  signUp: (
    name: string,
    username: string,
    email: string,
    password: string,
    dateOfBirth: string
  ) => Promise<{
    success: boolean;
    error?: string;
    processedError?: ProcessedError;
  }>;
  logout: () => void;
  /** True when we have a token and it is not expired (per JWT exp). Drives redirects and UI. */
  isAuthenticated: boolean;
  /** True when token exists but its exp is in the past. Useful for messaging ("Session expired"). */
  isTokenExpired: boolean;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

const readStoredToken = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  return localStorage.getItem(TOKEN);
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Lazy init matches LocaleProvider: visitAuthenticated() seeds localStorage in
  // onBeforeLoad, so the first render must see the token (useEffect would run
  // after ProtectedRoute already redirected to /login).
  const [token, setToken] = useState<string | null>(readStoredToken);
  const queryClient = useQueryClient();

  // TanStack Query: enabled only when we have a non-expired token. staleTime: Infinity means the
  // current user is fetched once per session and served from cache for all subsequent reads.
  // Strict Mode duplicate mounts are deduped by the shared query key (in-flight) and by the cache
  // (after settle). Logout calls removeQueries to clear the cache so the next login refetches once.
  const currentUserQuery = useQuery({
    queryKey: CURRENT_USER_QUERY_KEY,
    queryFn: () => unwrapServiceResultAsync(apiService.users.getCurrentUser()),
    enabled: !!token && !hasTokenExpired(token),
    staleTime: Infinity,
  });

  const user = currentUserQuery.data?.user ?? null;
  const loading = !!token && currentUserQuery.isLoading;

  // Locale + user persistence is owned by useSyncSessionPreferences (subscribes to the same
  // ['currentUser'] cache). Decoupled so auth changes don't risk breaking locale sync.
  useSyncSessionPreferences();

  const login = useCallback(
    async (
      usernameOrEmail: string,
      password: string
    ): Promise<{
      success: boolean;
      error?: string;
      processedError?: ProcessedError;
    }> => {
      const loginResult = await apiService.auth.login({
        usernameOrEmail,
        password,
      });

      if (!loginResult.success) {
        const withProcessed = loginResult as typeof loginResult & {
          processedError?: ProcessedError;
        };
        return {
          success: false,
          error: loginResult.error?.message ?? loginResult.message,
          processedError: withProcessed.processedError,
        };
      }

      // We unwrap the ServiceResult to get the access token.
      const accessToken = loginResult.data?.accessToken;
      if (!accessToken) {
        return {
          success: false,
          error: 'No token received',
          // There's no API error to run through the error handler, so there's no ProcessedError to pass.
          // Setting processedError to undefined keeps the rturn type consistent; Login already handles
          // missing processedError and will still show the message from result.error
          processedError: undefined,
        };
      }
      logger.info('Login successful');
      setToken(accessToken);
      localStorage.setItem(TOKEN, accessToken);
      // Let the useQuery (same key) run when token is set; it will fetch /user/me once and the effect
      // will sync locale/user to localStorage. Avoids duplicate GET /user/me from fetchQuery + useQuery.
      return { success: true };
    },
    []
  );

  const signUp = useCallback(
    async (
      name: string,
      username: string,
      email: string,
      password: string,
      dateOfBirth: string
    ): Promise<{
      success: boolean;
      error?: string;
      processedError?: ProcessedError;
    }> => {
      const result = await apiService.auth.signUp({
        name,
        username,
        email,
        password,
        dateOfBirth,
      });
      // Signup successful - user should now login with their credentials
      // The result.success indicates the account was created successfully
      if (result.success) {
        logger.info('Signup successful');
        return { success: true };
      }
      const withProcessed = result as typeof result & {
        processedError?: ProcessedError;
      };
      logger.error('Signup failed:', result.error?.message ?? result.message);
      return {
        success: false,
        error: result.error?.message ?? result.message,
        processedError: withProcessed.processedError,
      };
    },
    []
  );

  const logout = useCallback(() => {
    setToken(null);
    // Clear cached user so next login gets a fresh fetch.
    queryClient.removeQueries({ queryKey: CURRENT_USER_QUERY_KEY });
    localStorage.removeItem(TOKEN);
    localStorage.removeItem(USER);
    localStorage.removeItem(LOCALE);
  }, [queryClient]);

  useAutoLogoutOnExpiry(token, logout);

  const tokenExpiresAt = token ? getTokenExpiration(token) : null;
  const tokenExpired = hasTokenExpired(token);

  const value: AuthContextType = {
    user,
    token,
    tokenExpiresAt,
    login,
    signUp,
    logout,
    // Expired token => not authenticated; ProtectedRoute and RootRedirect will send user to login.
    isAuthenticated: !!token && !tokenExpired,
    isTokenExpired: tokenExpired,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
